import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useAuth } from '../hooks/useAuth.js';
import { useTheme } from '../hooks/useTheme.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 設定ページ。言語切替・アカウント情報表示・ログアウトを提供
 * @graph-connects useAuth [calls] 認証フック呼び出し
 */
export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

/** @graph-connects none */
const languageOptions = [
  { value: 'ja', label: '日本語' },
  { value: 'en', label: 'English' },
  { value: 'vi', label: 'Tiếng Việt' },
];

/** @graph-connects none */
const themeOptions = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
];

/** @graph-connects none */
function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, logout, isLoggingOut } = useAuth();
  const { theme, setTheme } = useTheme();

  // i18nに応じたラベルを動的に設定
  const localizedThemeOptions = themeOptions.map((opt) => ({
    ...opt,
    label: t(`settings.theme.options.${opt.value}`),
  }));

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('settings.title')}
        </h1>
        <p className="mt-1 text-[var(--color-text-secondary)]">{t('settings.description')}</p>
      </div>

      {/* Theme settings */}
      <Card>
        <CardHeader
          title={t('settings.theme.title')}
          description={t('settings.theme.description')}
        />
        <CardContent>
          <Select
            options={localizedThemeOptions}
            value={theme}
            onChange={(e) => {
              setTheme(e.target.value as 'dark' | 'light' | 'system');
            }}
          />
        </CardContent>
      </Card>

      {/* Language settings */}
      <Card>
        <CardHeader
          title={t('settings.language.title')}
          description={t('settings.language.description')}
        />
        <CardContent>
          <Select
            options={languageOptions}
            value={i18n.language.split('-')[0]}
            onChange={(e) => {
              void i18n.changeLanguage(e.target.value);
            }}
          />
        </CardContent>
      </Card>

      {/* Account info */}
      <Card>
        <CardHeader
          title={t('settings.account.title')}
          description={t('settings.account.description')}
        />
        <CardContent>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">{t('settings.account.name')}</p>
              <p className="text-[var(--color-text-primary)]">{user?.name ?? '-'}</p>
            </div>
            <div>
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('settings.account.email')}
              </p>
              <p className="text-[var(--color-text-primary)]">{user?.email ?? '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logout */}
      <Card>
        <CardHeader
          title={t('settings.logout.title')}
          description={t('settings.logout.description')}
        />
        <CardContent>
          <Button
            variant="danger"
            onClick={() => {
              logout();
            }}
            loading={isLoggingOut}
          >
            {t('auth.logout')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
